import { ChangeEvent, useContext, useEffect } from 'react';

import { Flex, FlexProps, HStack, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { Deal } from '../../../gql/dealGql';
import { DaysToPaymentEnum, getFirstPaymentDateISO } from '../../../gql/financialInfoGql';
import { Bank } from '../../../gql/generated/graphql';

import Select from '../../shared/Select';
import { DebouncedSave } from '../DealInfoBuyoutForm';
import { daysToPaymentOptions } from '../constants';

import { BuyRate } from './BuyRate';
import InputRow from './InputRow';

import { LDFlags } from '../../../constants/experiments';
import useFlag from '../../../hooks/useFlag';
import { DealActionsEnum, DealContext } from '../../../libs/DealContext';
import { formatDate } from '../../../libs/utils';
import { findBankSlug, getMaxBankMarkup, getMinBankMarkup } from '../../../utils/financialInfos';
import CreditDecisionsButton from '../../CreditDecisions/CreditDecisionsButton';

type BankAndPaymentsSectionProps = {
  debouncedSave: DebouncedSave;
  dealDetailsPageBeingEdited: boolean;
  inputIsReadOnlyByPermissions: boolean;
  banks: Bank[];
} & FlexProps;

export const BankAndPaymentsSection = ({
  debouncedSave,
  dealDetailsPageBeingEdited,
  inputIsReadOnlyByPermissions,
  banks,
  ...rest
}: BankAndPaymentsSectionProps) => {
  const stateDocFeeFlag = useFlag(LDFlags.STATE_DOC_FEE);
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Deal>();

  const { deal, dispatch, setUserChangedSomething } = useContext(DealContext);
  const bankSlug = findBankSlug({ banks, bankName: values.financial_info.bank });

  const handleDaysToPaymentChange = (
    e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>,
  ) => {
    setUserChangedSomething(true);

    const daysToPayment = parseInt(e?.target?.value || '0', 10) || DaysToPaymentEnum.FortyFive;
    setFieldValue('financial_info.days_to_payment', daysToPayment);
    setFieldValue(
      'financial_info.first_payment_date',
      getFirstPaymentDateISO(daysToPayment, bankSlug),
    );
  };

  useEffect(() => {
    const maxMarkup = getMaxBankMarkup({
      banks,
      bankName: values.financial_info.bank,
      term: values.financial_info.term,
    });
    const minMarkup = getMinBankMarkup({
      bankSlug,
      state: values.customer.address?.state,
      maxMarkup,
      stateDocFeeFlag,
    });

    setFieldValue('financial_info.maxMarkup', maxMarkup);
    setFieldValue('financial_info.minMarkup', minMarkup);
  }, [banks, values.financial_info.bank, values.financial_info.term]);

  useEffect(() => {
    // Triggers markup validations on first render and from that point on.
    setFieldTouched('financial_info.buy_rate', true, true);
    setFieldTouched('financial_info.newBuyRate', true, true);

    setFieldTouched('financial_info.sell_rate', true, true);
  }, [
    values.financial_info.maxMarkup,
    values.financial_info.minMarkup,
    // Using both to ensure the validation is triggered.
    deal.car.payoff.lienholder_name,
    values.car.payoff.lienholder_name,
  ]);

  useEffect(() => {
    if (deal.financial_info.maxMarkup !== values.financial_info.maxMarkup) {
      // Keep in sync the context value with the formik value so it can be used in the auto-save logic
      // Needed because the context value because is overwritten by other dispatches
      dispatch({
        type: DealActionsEnum.DeepUpdateDeal,
        payload: {
          financial_info: {
            maxMarkup: values.financial_info.maxMarkup,
          },
        },
      });
    }
  }, [deal.financial_info.maxMarkup, values.financial_info.maxMarkup]);

  useEffect(() => {
    if (
      deal.financial_info?.first_payment_date &&
      deal.financial_info?.first_payment_date !== values.financial_info?.first_payment_date
    ) {
      setFieldValue('financial_info.first_payment_date', deal.financial_info.first_payment_date);
    }
  }, [deal.financial_info?.first_payment_date]);

  return (
    <Flex direction="column" {...rest}>
      <InputRow label="Bank">
        <HStack alignItems="center" spacing={1}>
          <Text fontWeight="normal" fontSize="sm" pt={2} pl={3}>
            {deal.financial_info.bank}
          </Text>
          {dealDetailsPageBeingEdited ? (
            <CreditDecisionsButton isIconButton isEditingWithPermission />
          ) : null}
        </HStack>
      </InputRow>
      <InputRow label="Days To First Payment">
        <Select
          name="financial_info.days_to_payment"
          size="sm"
          options={daysToPaymentOptions}
          emptyOption={false}
          isDisabled={inputIsReadOnlyByPermissions || !dealDetailsPageBeingEdited}
          onChange={handleDaysToPaymentChange}
        />
      </InputRow>

      <InputRow label="First Payment Date">
        <Text variant="dealInfoBuyout">
          {formatDate(values.financial_info?.first_payment_date, 'utc')}
        </Text>
      </InputRow>
      <InputRow label="Buy Rate">
        <BuyRate
          dealDetailsPageBeingEdited={dealDetailsPageBeingEdited}
          debouncedSave={debouncedSave}
        />
      </InputRow>
    </Flex>
  );
};
